import isMobileAppRuntime, { getMobileAppPlatform } from './isMobileAppRuntime';

const PUSH_TOKEN_KEY = 'flcMobilePushToken';
const PUSH_PERMISSION_KEY = 'flcMobilePushPermission';
const PENDING_OTP_KEY = 'flcMobilePendingOtp';
const OTP_EVENT = 'flc:otp-code';
const OTP_CHANNEL_ID = 'flc-otp';
const NOTIFICATIONS_CHANNEL_ID = 'flc-mobile-notifications';

let initializationPromise = null;
let listenersInstalled = false;

const getPushPlugin = () => {
  if (typeof window === 'undefined') return null;
  return window.Capacitor?.Plugins?.PushNotifications || null;
};

const safeStorageGet = (key) => {
  try {
    return window.localStorage.getItem(key) || '';
  } catch (error) {
    return '';
  }
};

const safeStorageSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // Keep the active in-memory flow working even when localStorage is unavailable.
  }
};

const safeStorageRemove = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    // No-op.
  }
};

const normalizeOtp = (value) => {
  const match = String(value || '').match(/(?:^|\D)(\d{6})(?:\D|$)/);
  return match ? match[1] : '';
};

const extractOtpFromNotification = (notification = {}) => {
  const data = notification?.data || {};
  const candidates = [
    data.otp,
    data.code,
    data.verificationCode,
    data.verification_code,
    data.oneTimeCode,
    data.one_time_code,
    notification?.body,
    notification?.title,
  ];

  for (const candidate of candidates) {
    const otp = normalizeOtp(candidate);
    if (otp) return otp;
  }

  return '';
};

const publishOtp = (otp, source, notification = null) => {
  if (!otp || typeof window === 'undefined') return;

  const payload = {
    otp,
    source,
    receivedAt: new Date().toISOString(),
    challengeId: notification?.data?.challengeId || notification?.data?.challenge_id || null,
  };

  safeStorageSet(PENDING_OTP_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent(OTP_EVENT, { detail: payload }));
};

const installListeners = async (plugin) => {
  if (!plugin || listenersInstalled) return;
  listenersInstalled = true;

  await plugin.addListener('registration', (token) => {
    const value = String(token?.value || '').trim();
    if (value) safeStorageSet(PUSH_TOKEN_KEY, value);
    safeStorageSet(PUSH_PERMISSION_KEY, 'granted');
    window.dispatchEvent(new CustomEvent('flc:push-registration', { detail: { token: value } }));
  });

  await plugin.addListener('registrationError', (error) => {
    window.dispatchEvent(new CustomEvent('flc:push-registration-error', { detail: error || {} }));
  });

  await plugin.addListener('pushNotificationReceived', (notification) => {
    const otp = extractOtpFromNotification(notification);
    if (otp) publishOtp(otp, 'push-received', notification);
  });

  await plugin.addListener('pushNotificationActionPerformed', (action) => {
    const notification = action?.notification || {};
    const otp = extractOtpFromNotification(notification);
    if (otp) publishOtp(otp, 'push-tap', notification);
  });
};

const createAndroidOtpChannel = async (plugin) => {
  if (getMobileAppPlatform() !== 'android' || typeof plugin?.createChannel !== 'function') return;

  try {
    await plugin.createChannel({
      id: OTP_CHANNEL_ID,
      name: 'Verification codes',
      description: 'Login verification codes',
      importance: 5,
      visibility: 1,
      vibration: true,
    });
    await plugin.createChannel({
      id: NOTIFICATIONS_CHANNEL_ID,
      name: 'Notifications',
      description: 'Together we learn notifications',
      importance: 4,
      visibility: 1,
      vibration: true,
    });
  } catch (error) {
    console.warn('[mobile-push][channel-create-failed]', error);
  }
};

const requestPushRegistration = async (plugin) => {
  let permission = 'prompt';

  try {
    const current = await plugin.checkPermissions();
    permission = current?.receive || 'prompt';

    if (permission === 'prompt' || permission === 'prompt-with-rationale') {
      const requested = await plugin.requestPermissions();
      permission = requested?.receive || permission;
    }

    safeStorageSet(PUSH_PERMISSION_KEY, permission);

    if (permission === 'granted') {
      await plugin.register();
    }
  } catch (error) {
    safeStorageSet(PUSH_PERMISSION_KEY, 'error');
    console.warn('[mobile-push][registration-failed]', error);
  }

  return permission;
};

export const initializeMobilePushBridge = async () => {
  if (!isMobileAppRuntime()) {
    return { available: false, permission: 'web', token: '' };
  }

  if (initializationPromise) return initializationPromise;

  initializationPromise = (async () => {
    const plugin = getPushPlugin();
    if (!plugin) {
      return {
        available: false,
        permission: 'plugin-unavailable',
        token: safeStorageGet(PUSH_TOKEN_KEY),
      };
    }

    await installListeners(plugin);
    await createAndroidOtpChannel(plugin);
    const permission = await requestPushRegistration(plugin);

    return {
      available: true,
      permission,
      token: safeStorageGet(PUSH_TOKEN_KEY),
      channelId: OTP_CHANNEL_ID,
    };
  })();

  return initializationPromise;
};

export const getMobilePushContext = async ({ waitForRegistrationMs = 2500 } = {}) => {
  const initialized = await initializeMobilePushBridge();

  if (!initialized.available || initialized.token) {
    return {
      ...initialized,
      platform: getMobileAppPlatform(),
    };
  }

  const token = await new Promise((resolve) => {
    let finished = false;
    const finish = (value) => {
      if (finished) return;
      finished = true;
      window.removeEventListener('flc:push-registration', handleRegistered);
      window.removeEventListener('flc:push-registration-error', handleError);
      window.clearTimeout(timer);
      resolve(value || '');
    };
    const handleRegistered = (event) => finish(event?.detail?.token || safeStorageGet(PUSH_TOKEN_KEY));
    const handleError = () => finish('');
    const timer = window.setTimeout(() => finish(safeStorageGet(PUSH_TOKEN_KEY)), waitForRegistrationMs);

    window.addEventListener('flc:push-registration', handleRegistered);
    window.addEventListener('flc:push-registration-error', handleError);
  });

  return {
    ...initialized,
    token,
    permission: safeStorageGet(PUSH_PERMISSION_KEY) || initialized.permission,
    platform: getMobileAppPlatform(),
  };
};

export const consumePendingOtpNotification = () => {
  if (typeof window === 'undefined') return null;
  const raw = safeStorageGet(PENDING_OTP_KEY);
  if (!raw) return null;

  safeStorageRemove(PENDING_OTP_KEY);

  try {
    const parsed = JSON.parse(raw);
    return parsed?.otp ? parsed : null;
  } catch (error) {
    const otp = normalizeOtp(raw);
    return otp ? { otp, source: 'stored-push' } : null;
  }
};

export const MOBILE_OTP_EVENT = OTP_EVENT;
export const MOBILE_OTP_CHANNEL_ID = OTP_CHANNEL_ID;
export const MOBILE_NOTIFICATIONS_CHANNEL_ID = NOTIFICATIONS_CHANNEL_ID;

export const signInWithAppleNative = async () => {
  if (!isMobileAppRuntime() || getMobileAppPlatform() !== 'ios') {
    const error = new Error('Sign in with Apple is available only in the iOS mobile app.');
    error.code = 'APPLE_SIGN_IN_IOS_ONLY';
    throw error;
  }

  const capacitor = typeof window !== 'undefined' ? window.Capacitor : null;
  const applePlugin = capacitor?.Plugins?.FLCAppleSignIn;
  const hasPluginProxy = applePlugin && typeof applePlugin.signIn === 'function';
  const hasLegacyBridge = capacitor && typeof capacitor.nativePromise === 'function';

  if (!hasPluginProxy && !hasLegacyBridge) {
    const error = new Error('The native iOS bridge is not available in this build.');
    error.code = 'APPLE_SIGN_IN_BRIDGE_UNAVAILABLE';
    throw error;
  }

  // Capacitor's registered plugin proxy is the primary path. Keep nativePromise
  // only as a compatibility fallback for the older MVP shell.
  const nativeRequest = hasPluginProxy
    ? applePlugin.signIn({})
    : capacitor.nativePromise('FLCAppleSignIn', 'signIn', {});
  const timeout = new Promise((_, reject) => {
    window.setTimeout(() => {
      const error = new Error('Sign in with Apple did not respond. Rebuild the iOS app and try again.');
      error.code = 'APPLE_SIGN_IN_TIMEOUT';
      reject(error);
    }, 20000);
  });

  return Promise.race([nativeRequest, timeout]);
};
